import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { dayjs, formatTimeWithTimezone } from '@/utils/dateUtils';

export function ScreeningTrendIcon({ summaries }) {
  if (!summaries) return null;
  
  let worstTrend = 'stable';
  let tooltipText = '';
  
  for (const [type, data] of Object.entries(summaries)) {
    const typeName = type.toUpperCase().replace('_', '-');
    const d = data as Record<string, unknown>;
    if (d.trend === 'worsening') {
      worstTrend = 'worsening';
      tooltipText = `${typeName}: ${d.score}${d.previous_score ? ` (was ${d.previous_score})` : ''}`;
      break;
    } else if (d.trend === 'improving' && worstTrend !== 'worsening') {
      worstTrend = 'improving';
      tooltipText = `${typeName}: ${d.score}${d.previous_score ? ` (was ${d.previous_score})` : ''}`;
    } else if (!tooltipText) {
      tooltipText = `${typeName}: ${d.score}`;
    }
  }
  
  if (worstTrend === 'worsening') {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} title={tooltipText}>
        <TrendingDownIcon fontSize="small" color="warning" sx={{ fontSize: 14 }} />
      </Box>
    );
  } else if (worstTrend === 'improving') {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} title={tooltipText}>
        <TrendingUpIcon fontSize="small" color="success" sx={{ fontSize: 14 }} />
      </Box>
    );
  }
  
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }} title={tooltipText}>
      <TrendingFlatIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
    </Box>
  );
}

export function formatTime(isoString, appointmentTz) {
  if (!isoString) return '';
  if (!appointmentTz) {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const aptOffset = dayjs(isoString).tz(appointmentTz).utcOffset();
  const browserOffset = dayjs(isoString).tz(browserTz).utcOffset();
  const aptTimeStr = formatTimeWithTimezone(isoString, appointmentTz);
  if (aptOffset !== browserOffset) {
    const localTimeStr = formatTimeWithTimezone(isoString, browserTz);
    return `${aptTimeStr} / ${localTimeStr} local`;
  }
  return aptTimeStr;
}

export function formatTimeUntil(minutes) {
  if (minutes === null || minutes === undefined) return '';
  if (minutes < 0) return 'Started';
  if (minutes === 0) return 'Starting now';
  if (minutes === 1) return 'In 1 minute';
  if (minutes < 60) return `In ${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `In ${hours} hour${hours > 1 ? 's' : ''}`;
  return `In ${hours}h ${mins}m`;
}
